import type { ChatItem } from '../../../../contracts/harness.types';

import { planCreatePresentation } from './planCreatePresentation';
import { readTodoStateFromItem } from './todoState';
import type { TodoState } from './types';
import { CREATE_TODO_PLAN_TOOL_NAME, TODO_TOOL_NAME } from './types';

export interface LatestTodoPlan {
  anchorId: string;
  state: TodoState;
  /**
   * Final state each replaced plan reached, keyed by its anchor row. Superseded
   * plans stay in the transcript as frozen cards — only the latest plan drives
   * the pinned summary.
   */
  supersededByAnchorId: ReadonlyMap<string, TodoState>;
  /**
   * `todo` calls that precede the plan's first `todo_state`-carrying result.
   * The backend never backfills `todo_state`, so these are pre-launch history:
   * they stay on the legacy checklist rendering instead of being folded into
   * the card. Plan creates are never collected — a denied or timed_out
   * parseable create renders a frozen card; a real failure stays silent.
   */
  legacyIds: ReadonlySet<string>;
}

/**
 * True for todo-family tool uses that must render nothing at all — not even
 * the generic fallback row. Distinct from `resolveTodoToolUse` returning
 * `null`, which lets the item fall through to the approval / generic
 * renderers. Plan creates share one verdict with `resolveTodoToolUse` via
 * `planCreatePresentation` so queued-without-approved cannot leak a shimmer
 * row. Todo ops stay on the plan-card fold: only completed anchors render.
 */
export const isSuppressedTodoToolUse = (
  item: ChatItem,
  plan: LatestTodoPlan | null | undefined
): boolean => {
  if (item.kind !== 'tool_use') return false;
  const isPlanCreate = item.name === CREATE_TODO_PLAN_TOOL_NAME;
  if (item.name !== TODO_TOOL_NAME && !isPlanCreate) return false;
  if (isPlanCreate) return planCreatePresentation(item, plan).kind === 'suppress';
  if (item.status === 'pending_approval') return false;
  if (!plan) return false;
  if (plan.legacyIds.has(item.id)) return false;
  const isAnchor = item.id === plan.anchorId || plan.supersededByAnchorId.has(item.id);
  return !(isAnchor && item.status === 'complete');
};

/**
 * Walk the timeline and resolve the plan card's anchor row plus its latest
 * state. A `create_todo_plan` result re-anchors and replaces the plan
 * wholesale — the replaced plan's final state is kept in
 * `supersededByAnchorId` so its card stays in the transcript, frozen. A plain
 * `todo` result only advances the state, anchoring itself when the plan was
 * appended by ungated `todo create` calls and no anchor exists yet.
 *
 * The plan segment opens at the first result carrying `todo_state` — todo rows
 * before it are pre-launch history (`legacyIds`) and keep rendering as they
 * always did.
 */
export const computeLatestTodoPlan = (items: ChatItem[]): LatestTodoPlan | null => {
  let anchorId: string | null = null;
  let state: TodoState | null = null;
  let planStarted = false;
  const supersededByAnchorId = new Map<string, TodoState>();
  const legacyIds = new Set<string>();

  const closeSegment = () => {
    if (anchorId !== null && state !== null) supersededByAnchorId.set(anchorId, state);
    anchorId = null;
    state = null;
  };

  for (const item of items) {
    if (item.kind !== 'tool_use') continue;

    const isCreatePlan = item.name === CREATE_TODO_PLAN_TOOL_NAME;
    if (!isCreatePlan && item.name !== TODO_TOOL_NAME) continue;

    const parsed = item.status === 'complete' ? readTodoStateFromItem(item) : null;

    if (!parsed) {
      if (isCreatePlan && item.status === 'complete') {
        // A completed replacement is the boundary even when its state is
        // unreadable — freeze the plan it replaced rather than keep its card
        // live over steps that no longer exist.
        closeSegment();
        planStarted = true;
      } else if (!planStarted && !isCreatePlan) {
        legacyIds.add(item.id);
      }
      continue;
    }

    planStarted = true;
    if (isCreatePlan) closeSegment();
    if (isCreatePlan || anchorId === null) anchorId = item.id;
    state = parsed;
  }

  return anchorId !== null && state !== null
    ? { anchorId, state, supersededByAnchorId, legacyIds }
    : null;
};
